import axios from "axios";
import { supabase } from "./supabase";
import { reportError } from "./errorReporter";
import { ORG_BLOCKED_EVENT, isOrganizationBlockedError } from "./orgLifecycle";

const BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 30000,
});

// -----------------------------------------------------------------------------
// Browser storage — what & why (security note)
// -----------------------------------------------------------------------------
// We store ONLY the currently selected organization ID under ``quowada.orgId``.
// It is not a secret: knowing an org UUID does not grant any access — the
// backend independently validates that the caller's JWT ``sub`` is a member of
// that org via ``X-Organization-ID`` on every request.
//
// Supabase's auth session (JWT + refresh token) lives under Supabase's own
// storage key (``quowada.auth``) and is managed exclusively by the
// ``@supabase/supabase-js`` SDK. We NEVER manually copy, mirror or persist
// those tokens elsewhere — the request interceptor below always reads a fresh
// session directly from the SDK. That guarantees a single source of truth,
// avoids stale-token bugs, and keeps the current SPA token-storage tradeoff
// consistent (see the security note in the hardening report).
const ORG_KEY = "quowada.orgId";

export const orgStore = {
  get: () => localStorage.getItem(ORG_KEY),
  set: (v) => (v ? localStorage.setItem(ORG_KEY, v) : localStorage.removeItem(ORG_KEY)),
  clear: () => localStorage.removeItem(ORG_KEY),
};

api.interceptors.request.use(async (config) => {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (err) {
    // Intentionally non-fatal: if the SDK cannot deliver a session right now
    // (transient storage/decoding hiccup), we send the request without a
    // token. The backend will respond 401 and the response interceptor below
    // will attempt a single refresh. We NEVER log the error object here —
    // it may embed the failed token or a full storage snapshot.
    reportError("api.request.getSession", err);
  }
  const orgId = orgStore.get();
  if (orgId && !config.headers["X-Organization-ID"] && !config.skipOrgHeader) {
    config.headers["X-Organization-ID"] = orgId;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err.response?.status === 401) {
      try {
        await supabase.auth.refreshSession();
      } catch (refreshErr) {
        // Intentionally non-fatal — if refresh fails the SDK will emit an
        // ``onAuthStateChange`` with a null session and the AuthProvider
        // handles logging the user out. We must not print the refresh error
        // (it may contain the refresh-token blob in some browsers).
        reportError("api.response.refreshSession", refreshErr);
      }
    }
    // Phase 2D.1C — the organization lifecycle gate refused this request. Tell
    // the AuthProvider once; it refreshes the memberships so the app switches to
    // the Bulgarian lifecycle screen instead of retrying a blocked endpoint.
    if (isOrganizationBlockedError(err)) {
      try {
        window.dispatchEvent(new Event(ORG_BLOCKED_EVENT));
      } catch (dispatchErr) {
        reportError("api.response.organizationBlocked", dispatchErr);
      }
    }
    return Promise.reject(err);
  },
);

export default api;
